import type { App } from "vue"
import type { RouteRecordRaw } from "vue-router"

import { createAsyncModule } from "@peerup/helper"
import { SuiteAdapter } from "@peerup/suite/adapter"
import { createRouter, createWebHistory } from "vue-router"

import { setupVueRouterSentry } from "@/sentry"
import Loading from "@/views/module-loading/index.vue"
import SsoLoading from "@/views/module-sso-loading/index.vue"

import { createRouterGuards } from "./router-guards"

// 判断是否在微前端环境中
const isMicroFrontend = SuiteAdapter.isMicroApp()

// 登录相关路由
const loginRoute = {
  path: "/login",
  name: "Login",
  component: () => createAsyncModule(() => import("@/views/module-login/index.vue")),
  meta: {
    title: "",
  },
}

const invitationRoute = {
  path: "/invitation/:id",
  name: "Invitation",
  component: () => createAsyncModule(() => import("@/views/module-invitation/Invitation.vue")),
  meta: {
    title: "组织邀请",
    isPublic: true,
  },
}

const applicationRoute = {
  path: "/application/:id",
  name: "Application",
  component: () => createAsyncModule(() => import("@/views/module-invitation/Application.vue")),
  meta: {
    title: "组织邀请",
    isPublic: true,
  },
}

export const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: "/check_account",
  },
  {
    path: "/peerup-login",
    redirect: "/login?from=peerup",
  },
  {
    path: "/term",
    name: "Term",
    component: () => createAsyncModule(() => import("@/views/module-protocol/Term.vue")),
    meta: {
      title: "用户协议",
      hideInTabs: true,
      isPublic: true,
    },
  },
  {
    path: "/privacy",
    name: "Privacy",
    component: () => createAsyncModule(() => import("@/views/module-protocol/Privacy.vue")),
    meta: {
      title: "隐私政策",
      hideInTabs: true,
      isPublic: true,
    },
  },
  {
    path: "/",
    component: () => createAsyncModule(() => import("@/components/commonBox/index.vue")),
    children: [invitationRoute, applicationRoute],
  },
  {
    path: "/auth_redirect",
    name: "AuthRedirect",
    component: () => createAsyncModule(() => import("@/views/module-redirect/index.vue")),
    meta: {
      title: "权限校验",
      isPublic: true,
      // autoClose: true,
    },
  },
  {
    path: "/cxmt-redirect",
    name: "AuthRedirectUum",
    component: () => createAsyncModule(() => import("@/views/module-redirect/uum.vue")),
    meta: {
      title: "权限校验",
      isPublic: true,
      autoClose: true,
    },
  },
  {
    path: "/check_account",
    name: "CheckAccountStatus",
    component: Loading,
    meta: {
      title: "登录",
    },
  },
  {
    path: "/sso-loading",
    name: "SsoLoading",
    component: SsoLoading,
    meta: {
      title: "跳转登录中...",
    },
  },
  // 根据是否是微前端环境来决定路由结构
  ...(isMicroFrontend
    ? [loginRoute]
    : [
        {
          path: "/accounts",
          component: () => createAsyncModule(() => import("@/components/commonBox/index.vue")),
          children: [loginRoute],
        },
      ]),
  {
    path: "/error/404",
    name: "Error404",
    component: () => createAsyncModule(() => import("@/views/module-error/404.vue")),
    meta: {
      title: "404",
      hideInMenu: true,
      hideInTabs: true,
      isPublic: true,
    },
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/error/404",
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})
/**
 * 配置路由守卫, 通过判断meta.checkServer参数，检测是否需要重定向
 */

// reset router
export function resetRouter() {
  router.getRoutes().forEach(route => {
    const { name, meta } = route
    if (name && !meta.noLogin) {
      router.hasRoute(name) && router.removeRoute(name)
    }
  })

  setupVueRouterSentry(router)
}

export async function setupRouter(app: App<Element>) {
  // 创建路由守卫
  createRouterGuards(router)

  app.use(router)

  // 路由准备就绪后挂载APP实例
  await router.isReady()
}

/**
 * 获取路由的工具函数
 */
export function getRoute(path: string) {
  if (!router) {
    console.warn("Router not initialized")
    return null
  }

  const route = router.resolve(path)
  return route
}

/**
 * 获取当前路由
 * @returns
 */
export function getCurrentRoute() {
  if (!router) return null
  return router.currentRoute.value
}
/**
 * 获取当前路由 meta
 */
export function getCurrentMeta() {
  return getCurrentRoute()?.meta
}

export default router
